import { NgClass, NgStyle } from '@angular/common';
import { Component, Directive, DoCheck, ElementRef, HostListener, Injectable, Input, IterableDiffer, IterableDiffers, OnDestroy, forwardRef } from '@angular/core';
import { ScrollableDirective } from 'directives/scrollable.directive';
import { SelectListItemModel, sortSelectList } from 'models/SelectList';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Observable, Observer, Subscription } from 'rxjs';

/**
 * Represents a single visible dropdown list per application. Filters the content of the list, and allows highlighting and selecting items from the list for the active component
 **/
@Injectable()
export class DropdownListService {

	public width = 150;
	public top = 0;
	public left = 0;

	private autoSort = false;
	private observer?: Observer<SelectListItemModel>;
	private visible = false;
	private scrollSubscription?: Subscription;
	public show(selectList: SelectListItemModel[], autoSort: boolean, source: ElementRef<HTMLElement>, scrollParent?: ScrollableDirective): Observable<SelectListItemModel> {

		this.selectList = selectList;
		this.autoSort = autoSort;

		const boundingBox = source.nativeElement.getBoundingClientRect();
		this.width = boundingBox.width;
		this.left = boundingBox.left;
		this.top = boundingBox.bottom;

		this.scrollSubscription = scrollParent?.onScroll.subscribe(() => {
			const boundingBox = source.nativeElement.getBoundingClientRect();
			if (this.width === boundingBox.width && this.top === boundingBox.bottom && this.left === boundingBox.left) return;

			this.width = boundingBox.width;
			this.left = boundingBox.left;
			this.top = boundingBox.bottom;
		});

		this.visible = true;
		return new Observable<SelectListItemModel>(observer => this.observer = observer);
	}
	public hide() { this.visible = false; this.scrollSubscription?.unsubscribe(); }

	private _filter?: string;
	public set filter(value: string | undefined) {
		if (value === this._filter) return;
		this._filter = value;
		this.refreshFilter();
	}

	private refreshFilter() {
		if (this._filter === undefined) {
			this.filteredList = [];
		}
		else {
			this.filteredList = this.selectList.filter(a => ~a.name.toLocaleLowerCase().indexOf(this._filter!.toLocaleLowerCase()));
			this.sortList();
		}
	}
	sortList() {
		if (this.autoSort) sortSelectList(this.filteredList);
	}
	public get hidden() {
		return !this.filteredList || !this.visible || this._filter === undefined;
	}

	public filteredList: SelectListItemModel[] = [];
	private differ: IterableDiffer<SelectListItemModel>;
	private _selectList: SelectListItemModel[] = [];

	@Input() set selectList(list: SelectListItemModel[]) {
		this._selectList = list;
		this.refreshFilter();
	}
	get selectList() {
		return this._selectList;
	}
	public constructor(private iterableDiffers: IterableDiffers) {
		this.differ = this.iterableDiffers.find(this._selectList).create<SelectListItemModel>();
	}

	check() {
		if (this.differ.diff(this._selectList)) {
			this.refreshFilter();
		}
	}

	public selectItem(item: SelectListItemModel) {
		item.highlight = false;
		this.observer?.next(item);
		this.visible = false;
	}

	public selectHighlightedItem() {
		if (!this.filteredList || this.filteredList.length === 0) return;

		const currentItemIndex = this.getIndexOfHighlightedItem();
		this.removeHighlight();
		if (currentItemIndex > -1) this.selectItem(this.filteredList[currentItemIndex]);
	}

	public moveHighlight(direction: number) {
		if (!this.filteredList || this.filteredList.length === 0) return;

		const currentItemIndex = this.getIndexOfHighlightedItem();
		const newIndex = currentItemIndex + direction;
		this.removeHighlight();
		if (newIndex < 0) this.filteredList[this.filteredList.length - 1].highlight = true;
		else if (newIndex >= this.filteredList.length) this.filteredList[0].highlight = true;
		else this.filteredList[newIndex].highlight = true;
	}

	getIndexOfHighlightedItem() {
		return this.filteredList?.findIndex((item) => item.highlight) ?? -1;
	}

	removeHighlight() {
		this.selectList?.filter(item => item.highlight).forEach(item => {
			item.highlight = false;
		});
	}
}

/**
 * Actual pop-up list component displayed in the fixed overlay container
 **/
@Component({
   selector: 'dropdown-list',
   templateUrl: './dropdown-list.component.html',
   styleUrls: ['./dropdown-list.component.scss'],
   imports: [NgStyle, forwardRef(() => DropdownListItemDirective), NgClass, TranslatePipe]
})
export class DropdownListComponent implements DoCheck {
   constructor(public readonly dropdown: DropdownListService) { }

   ngDoCheck() {
	   this.dropdown.check();
   }
}

/**
 * Controls the DOM for each list item, allows clicking on item to select it, and scroll it into view when navigating with keyboard
 **/
@Directive({
   selector: '.list-item',
   exportAs: 'dropdownListItem'
})
export class DropdownListItemDirective implements OnDestroy {
   public constructor(private readonly list: DropdownListService, private readonly element: ElementRef<HTMLElement>) { }

   private subscription?: Subscription;
   private _item?: SelectListItemModel;
   @Input('item') set item(item: SelectListItemModel) {
	   this._item = item;
	   this.subscription = item.highlightChange.subscribe((highlighted) => {
		   if (!highlighted) return;

		   const element = this.element.nativeElement;
		   const parent = element.offsetParent!;

		   if (element.offsetTop + element.offsetHeight > parent.scrollTop + parent.clientHeight || element.offsetTop < parent.scrollTop) this.element.nativeElement.scrollIntoView({ block: 'nearest' });
	   });
   }

   @HostListener('click', ['$event']) click(event: MouseEvent) {
	   if (!this._item) return;
	   event.stopPropagation();
	   this.list.selectItem(this._item);
   }
   @HostListener('mousedown', ['$event']) mousedown(event: MouseEvent) { event.preventDefault(); }

   ngOnDestroy() {
	   this.subscription?.unsubscribe();
   }
}
